import { useMemo, useState } from 'react';
import { portalDs } from 'src/components/portal-design-system';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'src/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'src/components/ui/tooltip';
import { cn } from 'src/lib/utils';
import type { DataJobValidationResult } from '../types/data-jobs.types';
import {
  buildReconciliationMatrix,
  buildValidationMatrix,
  collectReconciliationCellResults,
  formatCheckTypeLabel,
  type ValidationMatrix,
  type ValidationMatrixCell,
} from '../helpers/validation-matrix.helpers';
import { DataJobStatusBadgeV2 } from './data-job-status-badge-v2';

type SelectedCell = {
  section: 'mart' | 'reconciliation';
  platform: string;
  table: string;
  checkType: string;
  cell: ValidationMatrixCell;
  related: DataJobValidationResult[];
};

function statusCellClasses(status: string | undefined): string {
  if (!status) {
    return 'border border-dashed border-[#D9DBE3] bg-[#F7F8FA] text-[#9CA3AF]';
  }
  const value = status.toLowerCase();
  if (['pass', 'success'].includes(value)) {
    return 'border border-[#BBF7D0] bg-[#F0FDF4] text-[#15803D] hover:bg-[#DCFCE7]';
  }
  if (['warning', 'warn'].includes(value)) {
    return 'border border-[#FDE68A] bg-[#FFFBEB] text-[#D97706] hover:bg-[#FEF3C7]';
  }
  if (['fail', 'failed', 'error'].includes(value)) {
    return 'border border-[#FECACA] bg-[#FEF2F2] text-[#DC2626] hover:bg-[#FEE2E2]';
  }
  return 'border border-[#D9DBE3] bg-[#EDEEF2] text-[#5E6278] hover:bg-[#E5E7EB]';
}

function formatMetricValue(value: unknown): string {
  if (value == null) return '—';
  if (typeof value === 'number') {
    if (Number.isInteger(value)) return value.toLocaleString();
    return value.toLocaleString(undefined, { maximumFractionDigits: 4 });
  }
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
}

function MatrixTable({
  title,
  description,
  matrix,
  section,
  selected,
  onSelect,
}: {
  title: string;
  description: string;
  matrix: ValidationMatrix;
  section: SelectedCell['section'];
  selected: SelectedCell | null;
  onSelect: (next: SelectedCell | null) => void;
}) {
  if (!matrix.rows.length || !matrix.checkTypes.length) {
    return null;
  }

  return (
    <div className="space-y-3">
      <div>
        <h4 className="text-[13px] font-semibold text-[#0D0F1A]">
          {title}
        </h4>
        <p className={cn(portalDs.caption, 'mt-0.5')}>
          {description}
        </p>
      </div>
      <div className={cn(portalDs.tableContainer, 'overflow-x-auto')}>
        <Table>
          <TableHeader>
            <TableRow className={portalDs.tableHead}>
              <TableHead className={cn(portalDs.tableHeadCell, 'sticky left-0 z-10 min-w-[160px] bg-[#F7F8FA]')}>
                Platform
              </TableHead>
              <TableHead className={cn(portalDs.tableHeadCell, 'min-w-[110px] bg-[#F7F8FA]')}>
                Table
              </TableHead>
              {matrix.checkTypes.map((checkType) => (
                <TableHead
                  key={checkType}
                  className={cn(portalDs.tableHeadCell, 'min-w-[110px] text-center')}
                  title={checkType}
                >
                  {formatCheckTypeLabel(checkType)}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {matrix.rows.map((row) => (
              <TableRow
                key={row.key}
                className={portalDs.tableBodyRow}
              >
                <TableCell className={cn(portalDs.tableCell, 'sticky left-0 z-10 bg-white font-medium')}>
                  {row.platform}
                </TableCell>
                <TableCell className={cn(portalDs.tableCell, 'text-[#5E6278]')}>
                  {row.table}
                </TableCell>
                {matrix.checkTypes.map((checkType) => {
                  const cell = row.cells[checkType];
                  const isSelected = Boolean(
                    selected
                    && selected.section === section
                    && selected.platform === row.platform
                    && selected.table === row.table
                    && selected.checkType === checkType
                  );
                  const label = cell?.status ?? 'n/a';
                  const tooltipText = cell?.result.message
                    ?? (cell ? `${row.platform}.${row.table} · ${checkType}: ${cell.status}` : 'No result');

                  return (
                    <TableCell
                      key={checkType}
                      className={cn(portalDs.tableCell, 'p-2 text-center')}
                    >
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            className={cn(
                              'inline-flex min-w-[4.5rem] items-center justify-center rounded-md px-2 py-1.5 text-[11px] font-semibold uppercase tracking-[0.02em] transition-colors',
                              statusCellClasses(cell?.status),
                              isSelected && 'ring-2 ring-[#0D0F1A] ring-offset-1'
                            )}
                            aria-label={`${row.platform} ${row.table} ${formatCheckTypeLabel(checkType)}: ${label}`}
                            aria-pressed={isSelected}
                            onClick={() => {
                              if (!cell) {
                                onSelect(null);
                                return;
                              }
                              if (isSelected) {
                                onSelect(null);
                                return;
                              }
                              onSelect({
                                section,
                                platform: row.platform,
                                table: row.table,
                                checkType,
                                cell,
                                related: [],
                              });
                            }}
                          >
                            {label}
                          </button>
                        </TooltipTrigger>
                        <TooltipContent
                          side="top"
                          className="max-w-sm rounded-lg bg-[#0D0F1A] px-3 py-2 text-left text-xs leading-relaxed text-white"
                        >
                          {tooltipText}
                        </TooltipContent>
                      </Tooltip>
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function SelectedCellDetail({
  selected,
}: {
  selected: SelectedCell;
}) {
  const { cell, related, platform, table, checkType } = selected;
  const { result } = cell;

  return (
    <div className="rounded-lg border border-[#EDEEF2] bg-[#F7F8FA] p-4">
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-[13px] font-semibold text-[#0D0F1A]">
          {platform}
          {' / '}
          {table}
          {' · '}
          {formatCheckTypeLabel(checkType)}
        </p>
        <DataJobStatusBadgeV2 status={cell.status} />
      </div>

      {result.message ? (
        <p className={cn(portalDs.bodySm, 'mt-2 text-[#0D0F1A]')}>
          {result.message}
        </p>
      ) : null}

      <dl className="mt-3 grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
        <div>
          <dt className={portalDs.filterLabel}>
            Value
          </dt>
          <dd className="mt-1 text-[13px] text-[#0D0F1A]">
            {formatMetricValue(result.value)}
          </dd>
        </div>
        <div>
          <dt className={portalDs.filterLabel}>
            Threshold
          </dt>
          <dd className="mt-1 text-[13px] text-[#0D0F1A]">
            {formatMetricValue(result.threshold)}
          </dd>
        </div>
        <div>
          <dt className={portalDs.filterLabel}>
            Active date
          </dt>
          <dd className="mt-1 text-[13px] text-[#0D0F1A]">
            {result.active_date ?? '—'}
          </dd>
        </div>
        <div>
          <dt className={portalDs.filterLabel}>
            Source
          </dt>
          <dd className="mt-1 text-[13px] text-[#0D0F1A]">
            {result.check_source}
          </dd>
        </div>
      </dl>

      {related.length > 1 ? (
        <div className="mt-4 space-y-2">
          <p className={portalDs.filterLabel}>
            Account results (
            {related.length}
            )
          </p>
          <div className="max-h-48 overflow-y-auto rounded-md border border-[#EDEEF2] bg-white">
            <Table>
              <TableHeader>
                <TableRow className={portalDs.tableHead}>
                  {['Account', 'Status', 'Value', 'Detail'].map((header) => (
                    <TableHead
                      key={header}
                      className={portalDs.tableHeadCell}
                    >
                      {header}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {related.map((item) => {
                  const account = String(
                    item.metrics?.account_id
                    ?? item.target.split('.').slice(1).join('.')
                    ?? item.target
                  );
                  return (
                    <TableRow
                      key={item.id}
                      className={portalDs.tableBodyRow}
                    >
                      <TableCell className={portalDs.tableCell}>
                        {account}
                      </TableCell>
                      <TableCell className={portalDs.tableCell}>
                        <DataJobStatusBadgeV2 status={item.status} />
                      </TableCell>
                      <TableCell className={cn(portalDs.tableCell, portalDs.tableCellMuted)}>
                        {formatMetricValue(item.value ?? item.metrics?.delta_pct)}
                      </TableCell>
                      <TableCell className={cn(portalDs.tableCell, portalDs.tableCellMuted, 'max-w-xs truncate')}>
                        {item.message ?? '—'}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </div>
      ) : null}

      {result.metrics && Object.keys(result.metrics).length > 0 && related.length <= 1 ? (
        <details className="mt-3">
          <summary className={cn(portalDs.caption, 'cursor-pointer select-none')}>
            Raw metrics
          </summary>
          <pre className="mt-2 overflow-x-auto rounded-md border border-[#EDEEF2] bg-white p-3 font-mono text-[11px] text-[#252840]">
            {JSON.stringify(result.metrics, null, 2)}
          </pre>
        </details>
      ) : null}
    </div>
  );
}

interface ValidationResultsMatrixProps {
  results: DataJobValidationResult[];
}

export function ValidationResultsMatrix({ results }: ValidationResultsMatrixProps) {
  const martMatrix = useMemo(() => buildValidationMatrix(results), [results]);
  const reconciliationMatrix = useMemo(
    () => buildReconciliationMatrix(results),
    [results]
  );
  const [selected, setSelected] = useState<SelectedCell | null>(null);

  const handleSelect = (next: SelectedCell | null) => {
    if (!next) {
      setSelected(null);
      return;
    }
    if (next.section === 'reconciliation') {
      setSelected({
        ...next,
        related: collectReconciliationCellResults(
          results,
          next.platform,
          next.checkType
        ),
      });
      return;
    }
    setSelected({ ...next, related: [next.cell.result] });
  };

  const hasMart = martMatrix.rows.length > 0 && martMatrix.checkTypes.length > 0;
  const hasRecon = reconciliationMatrix.rows.length > 0
    && reconciliationMatrix.checkTypes.length > 0;

  if (!hasMart && !hasRecon) {
    return (
      <p className={portalDs.bodySm}>
        No validation results recorded.
      </p>
    );
  }

  return (
    <TooltipProvider delayDuration={200}>
      <div className="space-y-6">
        {hasMart ? (
          <MatrixTable
            title="Mart consistency (internal)"
            description="Staging→mart integrity checks. Hover for a summary; click a cell for details. Does not contact vendors."
            matrix={martMatrix}
            section="mart"
            selected={selected}
            onSelect={handleSelect}
          />
        ) : null}

        {hasRecon ? (
          <MatrixTable
            title="Vendor reconciliation (external)"
            description="Worst status across accounts for each platform and metric vs vendor/export totals. Click a cell to inspect account-level drift."
            matrix={reconciliationMatrix}
            section="reconciliation"
            selected={selected}
            onSelect={handleSelect}
          />
        ) : null}

        {selected ? <SelectedCellDetail selected={selected} /> : null}
      </div>
    </TooltipProvider>
  );
}
